import { useMutation } from '@apollo/client';
import { useRef, useState, useEffect } from "react";
import { Column, createRecord, insertRecordBySeq, ResultTable, ScanRecord } from "./components/ResultTable";
import { Pda } from "./components/Pda";
import { parseException, clearCurrentError } from "utils/newExceptionUtils";
import { ScanInput } from "./components/ScanInput";
import { Button, Dialog, Input, Picker, Toast } from "antd-mobile";
import type { PickerValue } from 'antd-mobile/es/components/picker';
import { ScanningOutline, DownOutline, CloseCircleFill } from "antd-mobile-icons";
import { Header } from './components/Header';
import {isBagNumber, isTtBagNumber, isTrackingNumber} from '../../utils/BarCodeUtil';
import { useLazyQuery } from '@apollo/client';
import {
  GET_BAG_BASIC_INFO_BY_NUMBER,
  GetBagBasicInfoByNumberResponse,
  GetBagBasicInfoByNumberVariables,
  HANDLE_WHS_BAGGING_SCAN_EVENT_LEGACY,
  HandleWhsBaggingScanEventLegacyVariables,
  HandleWhsBaggingScanEventResponse,
  BagContainerCandidateType,
  UPDATE_BAG_CONTAINER_PDA,
  UpdateBagContainerVariables,
  UpdateBagContainerPdaResponse,
} from '../../graphql/queries/bagQueries';
import { playScanFailedAoSound, playRepeatedScanSound, playScanSound } from "../../utils/scanUtils";
import { t } from '../../i18n';
import { useScanQueueConsumer } from "./components/useScanQueueConsumer";
import { PdaScanEvent } from "./components/Pda";
import { getClientReporter } from '../../utils/clientReport/clientReporterRegistry';
import { ClientReportOpType } from '../../utils/clientReport/clientReportTypes';
import { safeJsonStringify, truncateToLength } from '../../utils/clientReport/clientReportUtils';
import { buildClientError, ClientError, ScanResultEnvelope, ScanResultPrompt, useScanResultPrompt } from './scanResult';
import type { BaggingScanResponse } from '../../graphql/queries/bagQueries';

// BaggingPage专用的错误Toast，支持关闭回调
let currentBaggingErrorToast: any = null;

const showBaggingError = (error: any, onClose?: () => void) => {
  // 先清除之前的Toast
  if (currentBaggingErrorToast) {
    currentBaggingErrorToast.close();
    currentBaggingErrorToast = null;
  }

  // 若传入的已是 { title, message } 纯对象，直接使用；否则交由 parseException 解析 ApolloError
  const { title, message } = (error && typeof error === 'object' && typeof error.title === 'string' && typeof error.message === 'string')
    ? error
    : parseException(error);
  
  currentBaggingErrorToast = Toast.show({
    content: (
      <div className="relative py-2" style={{ minWidth: '200px', maxWidth: '80vw' }}>
        <div className="flex items-center justify-center mb-2 relative">
          <div className="flex items-center gap-2">
            <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
            <div className="text-base">{title}</div>
          </div>
          <div
            className="absolute right-0 cursor-pointer flex items-center justify-center w-6 h-6"
            onClick={() => {
              if (currentBaggingErrorToast) {
                currentBaggingErrorToast.close();
                currentBaggingErrorToast = null;
              }
              onClose?.();
            }}
          >
            <span className="text-white text-lg">✕</span>
          </div>
        </div>
        <div className="text-sm text-center px-4">{message}</div>
      </div>
    ),
    duration: 0 // 常驻直到关闭
  });
};

const clearBaggingError = () => {
  if (currentBaggingErrorToast) {
    currentBaggingErrorToast.close();
    currentBaggingErrorToast = null;
  }
};


export interface HandleScanEventResult {
  /** mutation 返回的整个外壳；业务成功 / 失败 / 系统异常都用同一结构 */
  envelope: ScanResultEnvelope<BaggingScanResponse> | null;
}

const baggingScanClientReporter = getClientReporter(ClientReportOpType.BAGGING_SCAN);

interface PageBodyProps {
  scanPrompt: ReturnType<typeof useScanResultPrompt>;
}

const PageBody = ({ scanPrompt }: PageBodyProps) => {

  const [handleWhsBaggingScanEvent] = useMutation<HandleWhsBaggingScanEventResponse, HandleWhsBaggingScanEventLegacyVariables>(HANDLE_WHS_BAGGING_SCAN_EVENT_LEGACY);
  const [getBagBasicInfo] = useLazyQuery<GetBagBasicInfoByNumberResponse, GetBagBasicInfoByNumberVariables>(GET_BAG_BASIC_INFO_BY_NUMBER);
  // 与 PC 包牌列表同一入口：换容器立即落库；失败时回滚本地 state
  const [updateBagContainerPda] = useMutation<UpdateBagContainerPdaResponse, UpdateBagContainerVariables>(UPDATE_BAG_CONTAINER_PDA);

  const handleScanEvent = async (
    value1: string,
    value2: string,
    clientReportKey: string,
    bagContainerId?: number | null,
  ): Promise<HandleScanEventResult> => {
    const { data } = await handleWhsBaggingScanEvent({
      variables: {
        bagNumber: value1,
        trackingNumber: value2,
        clientReportKey,
        bagContainerId: bagContainerId ?? null,
      },
    });
    // mutation 直接返回 BaggingScanResponse，包装成 ScanResultEnvelope 供 showResult 使用
    const raw = data?.result ?? null;
    return { envelope: raw ? { success: raw.success, message: raw.message ?? null, data: raw } : null };
  };

  const scanPromptRef = useRef(scanPrompt);
  scanPromptRef.current = scanPrompt;

  const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
  const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
  const [bagNumber, setBagNumber] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [frameColor, setFrameColor] = useState<'red' | null>(null);

  // PPMOD 容器候选：由 getBagBasicInfoByNumber 响应带出（后端按 bag.externalBusinessType 决定：
  // PP → operateType=TEMU 的容器列表，非 PP → 空数组）；更换包牌时随 useEffect 重新填充
  const [containerCandidates, setContainerCandidates] = useState<BagContainerCandidateType[]>([]);
  const [selectedContainerId, setSelectedContainerId] = useState<number | null>(null);
  const [containerPickerVisible, setContainerPickerVisible] = useState(false);
  // 后端 bag.id（GET_BAG_BASIC_INFO_BY_NUMBER 返回）；换容器 mutation 按 id 定位。老数据/异常路径可能为 null，调用前需判空
  const [backendBagId, setBackendBagId] = useState<number | null>(null);
  // PP 包标识 + 分拣码（用于 1c 二次确认文案 "{分拣码}{包牌号}将改为{容器类型}"）
  const [isPPMODBag, setIsPPMODBag] = useState(false);
  const [bagSortingCode, setBagSortingCode] = useState<string>('');

  // ref to read latest bagNumber inside async onProcess callback
  const bagNumberRef = useRef(bagNumber);
  bagNumberRef.current = bagNumber;
  const selectedContainerIdRef = useRef(selectedContainerId);
  selectedContainerIdRef.current = selectedContainerId;
  const isPPMODBagRef = useRef(isPPMODBag);
  isPPMODBagRef.current = isPPMODBag;

  const clearFrame = () => setFrameColor(null);

  // 组件卸载时清理错误弹窗
  useEffect(() => {
    return () => {
      clearCurrentError();
      clearBaggingError();
    };
  }, []);

  // 包牌变化时：从 getBagBasicInfoByNumber 一次性获取容器候选 + 已绑容器 id + isPPMOD + sortingCode
  // 后端按 bag.externalBusinessType 过滤：PP → TEMU 容器列表，非 PP → 空数组
  //
  // 错误级响应（success=false + promptInfo，如 BAG_NOT_EXIST）：走 scanPrompt + 失败音效展示；
  // 300ms 防抖避免键入过程中每个 keystroke 都触发查询/报错。
  useEffect(() => {
    if (!bagNumber || bagNumber.trim() === '') {
      setContainerCandidates([]);
      setSelectedContainerId(null);
      setIsPPMODBag(false);
      setBagSortingCode('');
      setBackendBagId(null);
      return;
    }
    let cancelled = false;
    const clearDerived = () => {
      setContainerCandidates([]);
      setSelectedContainerId(null);
      setIsPPMODBag(false);
      setBagSortingCode('');
      setBackendBagId(null);
    };
    const timer = setTimeout(async () => {
      if (cancelled) return;
      try {
        const { data } = await getBagBasicInfo({ variables: { bagNumber } });
        if (cancelled) return;
        const info = data?.getBagBasicInfoByNumber;
        // 后端错误（BAG_NOT_EXIST 等）：展示 promptInfo + 失败音效；清空派生状态
        if (info && info.success === false && info.promptInfo) {
          playScanFailedAoSound();
          scanPromptRef.current.showResult(
            { success: false, message: info.message ?? null, data: { promptInfo: info.promptInfo } } as any,
            bagNumber,
          );
          clearDerived();
          return;
        }
        // 成功：绿框 + 成功音效 + 填充派生状态
        // showResult 内部会根据 promptInfo.popupType（0=SUCCESS）走绿框成功路径
        if (info?.success === true) {
          playScanSound();
          scanPromptRef.current.showResult(
            { success: true, message: info.message ?? null, data: { promptInfo: info.promptInfo } } as any,
            bagNumber,
          );
        }
        setContainerCandidates(info?.containerCandidates ?? []);
        setSelectedContainerId(info?.bagContainerId ?? null);
        setIsPPMODBag(!!info?.isPPMOD);
        setBagSortingCode(info?.promptSortingCode || info?.sortingCode || '');
        setBackendBagId(info?.id != null ? Number(info.id) : null);
      } catch {
        if (!cancelled) clearDerived();
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [bagNumber, getBagBasicInfo]);


  const onProcess = async (event: PdaScanEvent) => {
    const barCode = event.code;

    // PDA 硬件扫描绕过 ScanInput.onScan 直接入队，包牌/运单号分流必须在此统一处理
    if (isBagNumber(barCode) || isTtBagNumber(barCode)) {
      // 清除之前的错误状态
      clearCurrentError();
      clearBaggingError();
      setFrameColor(null);
      scanPromptRef.current.clearPrompt();
      setBagNumber(barCode);
      return;
    }
    // 包牌已扫、或条码属于运单号域（SWX 前缀）时跳过外部包牌查询，直接走运单号逻辑
    if (bagNumberRef.current === '') {
      // 不是已知格式的包牌号，尝试查后端判断是否为外部包牌号
      try {
        const bagQueryResult = await getBagBasicInfo({variables: {bagNumber: barCode}});
        const bagResp = bagQueryResult.data?.getBagBasicInfoByNumber;
        if (bagResp?.success && bagResp?.bagNumber) {
          clearCurrentError();
          clearBaggingError();
          setFrameColor(null);
          scanPromptRef.current.clearPrompt();
          setBagNumber(barCode);
          return;
        }
      } catch {
        // 查询失败则继续走运单号逻辑
      }
    }
    if (!isTrackingNumber(barCode)) {
      playScanFailedAoSound();
      scanPromptRef.current.showResult(
        buildClientError(ClientError.INVALID_TRACKING_NUMBER, { barCode }),
        barCode,
      );
      return;
    }

    // 兼容收起键盘
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const clientReportKey = baggingScanClientReporter.createReportKey(barCode);
    void baggingScanClientReporter.onScanTriggered(clientReportKey, barCode);

    const currentBagNumber = bagNumberRef.current;
    if (currentBagNumber === '') {
      playScanFailedAoSound();
      scanPromptRef.current.showResult(
        buildClientError(ClientError.BAG_CODE_REQUIRED),
        barCode,
      );
      void baggingScanClientReporter.onIntercepted(clientReportKey, 'bag_code_required');
      return;
    }

    // PP 包必须先选容器类型，未选则前置拦截（对齐 PRD "扫包裹时，需要先选择容器类型，否则报错"）
    // 非 PP 包无此约束；后端 requireBagContainerSelectedForPP 是兜底
    if (isPPMODBagRef.current && selectedContainerIdRef.current == null) {
      playScanFailedAoSound();
      const title = t('pda.pages.bagging.selectContainerType') || 'Please select container type';
      scanPromptRef.current.showResult(
        { success: false, message: title, data: {
          promptInfo: { messageKey: 'PP_BAG_CONTAINER_NOT_SELECTED', popupType: 1, title, content: title },
        } } as any,
        barCode,
      );
      void baggingScanClientReporter.onIntercepted(clientReportKey, 'pp_container_required');
      return;
    }

    setTrackingNumber(barCode);

    // 清除之前的错误
    clearCurrentError();
    clearBaggingError();
    setFrameColor(null);
    scanPromptRef.current.clearPrompt();

    void baggingScanClientReporter.onRequest(clientReportKey, { trackingNumber: barCode, bagNumber: currentBagNumber });
    let responseData: any = null;
    try {
      // PPMOD 非必填容器：未选传 null，后端不改动 bag.bag_container_id；已选则透传
      const { envelope } = await handleScanEvent(currentBagNumber, barCode, clientReportKey, selectedContainerIdRef.current);
      responseData = envelope;
      if (envelope?.success) {
        // 成功（含 ALREADY_SCANNED 等幂等成功，popupType=SUCCESS）
        scanPromptRef.current.showResult(envelope as ScanResultEnvelope<BaggingScanResponse>, barCode);
        const matchedBagNumber = (envelope.data as any)?.matchedBagNumber;
        if (matchedBagNumber) {
          // 真实装包成功才写成功列
          setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({ value1: currentBagNumber, value2: barCode, seq: event.seq })));
        }
      } else if (envelope) {
        // 业务失败：用后端 promptInfo 走 ScanResultPrompt，红/橙样式与 SortingScan 一致
        scanPromptRef.current.showResult(envelope as ScanResultEnvelope<BaggingScanResponse>, barCode);
        const promptInfo = (envelope.data as any)?.promptInfo;
        const reason = [promptInfo?.title, promptInfo?.content].filter(Boolean).join('，') || envelope.message || (t('pda.common.failed') || '');
        if (promptInfo?.messageKey === 'ALREADY_SCANNED') {
          playRepeatedScanSound();
        } else {
          playScanFailedAoSound();
        }
        setFailedRecords(prev => insertRecordBySeq(prev, createRecord({ value1: currentBagNumber, value2: barCode, failedReason: reason, seq: event.seq })));
      }
    } catch (error) {
      // 网络 / Apollo 异常兜底（后端外壳没回，走系统级 message）
      const { message } = parseException(error);
      scanPromptRef.current.showResult(buildClientError(ClientError.NETWORK_ERROR, {}, ClientError.BAGGING_SCAN_TITLE), barCode);
      playScanFailedAoSound();
      responseData = { error: String(error), message };
      setFailedRecords(prev => insertRecordBySeq(prev, createRecord({ value1: currentBagNumber, value2: barCode, failedReason: message, seq: event.seq })));
    } finally {
      baggingScanClientReporter.onResponse(clientReportKey, responseData || {})
        .catch(() => {
          void baggingScanClientReporter.reportDirectly(clientReportKey, {
            responseAtMs: Date.now(),
            responseResult: responseData ? truncateToLength(safeJsonStringify(responseData), 300) : ''
          });
        });
    }
  };

  const { enqueueScan } = useScanQueueConsumer({ maxConcurrency: 1, onProcess });

  const successColumns: Column[] = [
    { title: t('pda.pages.bagging.bagCode'), key: 'value1' },
    { title: t('pda.pages.bagging.trackingNO.'), key: 'value2' },
    { title: t('pda.common.time'), key: 'time' }
  ];

  const failedColumns = [
    ...successColumns,
    { title: t('pda.common.failedReason'), key: 'failedReason' }
  ];

  const handleBagCodeChange = (value: string) => {
      setBagNumber(value);
  };

  const frameStyle = frameColor === 'red' ? { borderColor: '#c7381e' } : undefined;

  return (
    <div className="flex flex-col gap-2">
      {/* 红色边框覆盖层 */}
      {frameColor && (
        <div className={`fixed inset-0 border-8 pointer-events-none z-[1100] ${frameColor === 'red' ? '' : 'border-red-500'}`} style={frameStyle} />
      )}

      <div className="flex items-center mb-2 border rounded">
        <Input
          className="flex-1 p-2"
          style={{ fontSize: '10px' }}
          placeholder={t('pda.pages.bagging.bagCode')}
          value={bagNumber}
          onChange={handleBagCodeChange}
        />
        <Button
          fill={"none"}
          className="ml-2 bg-transparent border-0"
        >
          <ScanningOutline fontSize={24} />
        </Button>
      </div>
      {/* PPMOD 容器类型（非必填）：参考 ExceptionScanComponent 异常下拉的实现（Picker + readOnly Input + DownOutline） */}
      <div
        className="flex items-center mb-2 border rounded cursor-pointer"
        onClick={() => {
          if (containerCandidates.length > 0) setContainerPickerVisible(true);
        }}
      >
        <Input
          className="flex-1 p-2"
          style={{ fontSize: '10px', cursor: 'pointer' }}
          placeholder={t('pda.pages.bagging.selectContainerType') || 'Please select container type'}
          value={
            selectedContainerId != null
              ? (containerCandidates.find(c => c.id === selectedContainerId)?.name || '')
              : ''
          }
          readOnly
        />
        <Button
          fill="none"
          className="ml-2 bg-transparent border-0"
          onClick={() => {
            if (containerCandidates.length > 0) setContainerPickerVisible(true);
          }}
        >
          <DownOutline fontSize={24} />
        </Button>
      </div>
      <Picker
        columns={[containerCandidates.map(c => ({ label: c.name, value: c.id }))]}
        visible={containerPickerVisible}
        onClose={() => setContainerPickerVisible(false)}
        value={selectedContainerId != null ? [selectedContainerId] : []}
        title={t('pda.pages.bagging.selectContainerType') || 'Please select container type'}
        onConfirm={async (value: PickerValue[]) => {
          const next = value[0] as number | null;
          if (next == null || next === selectedContainerId) return;
          // 已选过容器再改：弹二次确认（PRD 1c "更改时弹窗二次确认：{分拣码}{包牌号}将改为{容器类型}"）
          if (selectedContainerId != null) {
            const newName = containerCandidates.find(c => c.id === next)?.name || '';
            const confirmMsgTemplate = t('pda.pages.bagging.changeContainerConfirm')
              || '{{sortingCode}} {{bagNumber}} will be changed to {{containerType}}';
            const confirmMsg = confirmMsgTemplate
              .replace('{{sortingCode}}', bagSortingCode || '-')
              .replace('{{bagNumber}}', bagNumber || '-')
              .replace('{{containerType}}', newName);
            const ok = await Dialog.confirm({
              title: t('pda.pages.bagging.changeContainerTitle') || 'Change container type',
              content: confirmMsg,
              confirmText: t('pda.common.confirm'),
              cancelText: t('pda.common.cancel'),
            });
            if (!ok) return;
          }
          // 缺少后端 bag.id 无法调 updateBagContainer（老数据/异常路径兜底）：
          // 走 ScanResultPrompt 错误级弹层，仅展示 title（i18n content 为空自动隐藏），不动 state
          if (backendBagId == null) {
            playScanFailedAoSound();
            scanPromptRef.current.showResult(
              buildClientError(ClientError.CHANGE_CONTAINER_NO_BAG_ID),
              bagNumber,
            );
            return;
          }
          // 乐观更新本地 state，同时调用 PDA 版 updateBagContainerPDA 落库；
          // 失败：优先展示后端渲染的 promptInfo（照抄 OldBaggingScanPage.tsx:284-285 的 getBagBasicInfoByNumber 处理模式），
          // 兜底走 buildClientError（i18n_config 未 seed 或 catch 到系统级异常时）
          const prevContainerId = selectedContainerId;
          setSelectedContainerId(next);
          try {
            const resp = await updateBagContainerPda({
              variables: { bagId: backendBagId, bagContainerId: next },
            });
            const result = resp.data?.updateBagContainerPDA;
            if (!result?.success) {
              setSelectedContainerId(prevContainerId);
              playScanFailedAoSound();
              if (result?.promptInfo) {
                // 后端已按 messageKey + details 从 i18n_config 渲染好 title/content，直接展示
                scanPromptRef.current.showResult(
                  { success: false, data: { promptInfo: result.promptInfo } },
                  bagNumber,
                );
              } else {
                // promptInfo 缺失兜底（不应到达，除非 i18n_config 完全未 seed 时的极端情况）
                scanPromptRef.current.showResult(
                  buildClientError(ClientError.CHANGE_CONTAINER_FAILED, { errorMessage: '' }),
                  bagNumber,
                );
              }
            } else if (result.promptInfo) {
              // 成功：复用后端 runScanDirect 渲染的 SUCCESS promptInfo（popupType=SUCCESS），
              // 走 ScanResultPrompt 成功样式，与其它扫码成功保持一致
              scanPromptRef.current.showResult(
                { success: true, data: { promptInfo: result.promptInfo } },
                bagNumber,
              );
            }
          } catch (e) {
            setSelectedContainerId(prevContainerId);
            playScanFailedAoSound();
            scanPromptRef.current.showResult(
              buildClientError(
                ClientError.CHANGE_CONTAINER_FAILED,
                { errorMessage: (e as Error)?.message || '' },
              ),
              bagNumber,
            );
          }
        }}
      />
      <ScanInput placeholder={t('pda.pages.bagging.trackingNO.')} value={trackingNumber} onScan={enqueueScan} />

      <ResultTable
        successRecords={successRecords}
        failedRecords={failedRecords}
        successColumns={successColumns}
        failedColumns={failedColumns}
      />
    </div>
  );

}

export default function BaggingPage() {
  const scanPrompt = useScanResultPrompt();
  return (
    <div className="w-full h-full flex flex-col gap-2 p-2 relative">
      {/* 业务失败 / 系统异常的红/橙/黑底反馈层（与 SortingScan 同款），z-[60] 盖在原有 frame 之上 */}
      <ScanResultPrompt prompt={scanPrompt.prompt} barCode={scanPrompt.barCode} onClose={scanPrompt.clearPrompt} />
      <Header title={t('pda.pages.bagging.baggingScan')} />
      <Pda />
      <PageBody scanPrompt={scanPrompt} />
    </div>
  );
}